import { AuthService } from '@skinsoft/auth';
import { fetch, Proxy } from '@skinsoft/network';
import { identity, isEqual, merge as lodashMerge } from 'lodash';
import moment from 'moment';
import { EMPTY, from, iif, merge, Observable, of, Subject, Subscription, throwError } from 'rxjs';
import {
    catchError,
    concatMap,
    delayWhen,
    distinctUntilChanged,
    filter,
    first,
    last,
    map,
    mergeMap,
    pluck,
    switchMap,
    tap,
} from 'rxjs/operators';
import { DataSynchronisationStatus, OfflineDataFetcher } from './offline/offline-data-fetcher'

import { OfflineDataUpdateCacher } from '@skinsoft/data-store/src/offline/offline-data-update-cacher';
import _ from 'lodash';
import { IndexedDBCache } from './cache/indexeddb-cache';
import { Cache } from './cache/types';

type FragmentParser<T> = (response: any) => T[];

declare const FALLBACK_LOCALE;

export interface SearchResponse<T> {
    totalSize: number,
    allData: T[],
    results: T[]
}

export interface DataSynchroEvent {
    status: DataSynchronisationStatus,
    name: string
}

interface CachedEntityData<T> {
    id: string;
    body: T;
}

export const dataSynchroEventBus$ = new Subject<DataSynchroEvent>();

const SEARCH_PAGE_SIZE: number = 100;

export interface PushResult {
    nbToPush: number;
    nbPushSuccesses: number;
}

export interface DataStoreOptions {
    endpoint?: string;
    query?: Record<string, any>;
    emulateSearch?: boolean;
}

declare var WORKSPACE_ID: string;

export const DEFAULT_PROXY = new Proxy();
/**
 * Base class for all data management in the app.
 * Generics : 
 *  - T : principal type of the entity managed by this class
 *  - S : secondary type retrieved when performing a search query (if not specified, it is equals to T by default)
 */
export abstract class DataStore<T = any, S = T> {

    public get sanitizedIdentifer() {
        // For a strange reason, 'replaceAll' doesn't exist in Chrome for now, so we cheat
        const sanitized = this.endpoint.split('/').join('');
        return sanitized
    }

    public get getNbEntitiesInUpdateCache() {
        return this.nbEntitiesInUpdateCache
    }

    public get getNbLocallyCreatedEntities() {
        return this.nbCreatedEntitiesInUpdateCache;
    }

    public get isSyncing() {
        return this.syncing;
    }

    private get mainCacheKey() {
        return this.endpoint;
    }

    public get lastSyncStats() {
        return this.offlineDataFetcher.getLastSynchroStats();
    }

    protected proxy = DEFAULT_PROXY;
    protected endpoint: string;
    protected cache: Cache<T>;

    //// OFFLINE //////////////////////////////////
    protected offlineDataFetcher: OfflineDataFetcher<T, S>;
    protected offlineDataUpdateCacher: OfflineDataUpdateCacher<T>;
    protected emulateSearch: boolean = false;

    // Stats
    private currentNbSychronized: number = 0;
    private maxNbToSynchronize: number = Infinity;
    private fragmentParser: FragmentParser<T>;
    private userGroupId: string;
    private typesForSearch: string[]

    // Offline mode 
    private globalPushing = false;

    private dataStore: DataStore<T>;
    private syncing: boolean = false;
    private pushing: boolean = false;
    private pushresult: PushResult = { nbPushSuccesses: 0, nbToPush: 0 };
    private nbEntitiesInUpdateCache: number = 0;
    private nbCreatedEntitiesInUpdateCache: number = 0;

    private lastSynchroStats: any = null;
    private lastPushDate: number = 0;


    private executionStartTime: number = 0;

    private currentSynchroSubscription: Subscription = null as any;

    private abortSync: boolean = false;

    constructor(
        endpoint: string,
        typesForSearch?: string[],
        fragmentParser: FragmentParser<T> = identity,
        proxy = DEFAULT_PROXY,
        cache: Cache<T> = new IndexedDBCache<T>(),
    ) {
        this.fragmentParser = fragmentParser;
        this.endpoint = endpoint;
        this.cache = cache;
        this.proxy = proxy;
        if (typesForSearch) {
            this.typesForSearch = typesForSearch;
        }

        // Fetching user group
        AuthService.getInstance().user$.pipe(
            switchMap((user) => {
                if (user) {
                    return this.proxy.fetch("/groups/userGroup", {
                        userId: user.id,
                        workspace: WORKSPACE_ID
                    })
                } else {
                    return EMPTY;
                }
            })
        ).subscribe((group) => {
            this.userGroupId = group.id;
        });

        // Offline mode
        this.initializeDataFetcher();
        this.initializeDataPusher();

    }

    /**
     * @deprecated This method fetches all data of one type. This can lead to performance issues 
     * or crash if data is very numerous. The api endpoint will probably be removed in future.
     * @param options 
     * @param method 
     */
    public fetchAll(options: DataStoreOptions = {}, method = 'GET'): Observable<T[]> {

        const searchParams = new URLSearchParams(options.query);
        searchParams.set('archived', 'false');

        const endpoint = options.endpoint || this.endpoint;
        const cacheKey = `${endpoint}?${searchParams.toString()}`;

        return merge(
            this.proxy.fetch(endpoint, method === 'GET' ? searchParams : undefined, method, method !== 'GET' ? options.query : undefined).pipe(
                map(this.fragmentParser),
                // tap(data => this.cache.write(cacheKey, data)),
                catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
            ),
            // this.cache$(cacheKey),
        ).pipe(distinctUntilChanged(isEqual));
    }

    public fetchOne(id: string, networkFirst = false): Observable<T> {
        // if online
        if (window.navigator.onLine && !OfflineDataFetcher.isTemporaryId(id)) {
            const uri = `${this.endpoint}/${id}`;

            const network$ = this.proxy.fetch(uri, undefined, undefined, undefined, networkFirst).pipe(
                tap(data => this.offlineDataFetcher.updateOne(data.id, data)),
                last(),
                catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
            );
            let source$ = network$;

            if (networkFirst) {
                source$ = source$.pipe(delayWhen(() => network$));
            }

            return source$.pipe(
                distinctUntilChanged(isEqual),
                this.mergeUpdateCacheDataToOneResult.bind(this),
            );
        }
        // if offline
        else {
            return iif(
                () => OfflineDataFetcher.isTemporaryId(id),
                this.offlineDataUpdateCacher.fetchOneFromId(id),
                this.offlineDataFetcher.fetchOneFromId(id)
            ).pipe(
                switchMap(async (data: any) => {
                    // merging values from update-cache with value from fetch-cache not to loose information
                    const cachedUpdatedValue = await this.offlineDataUpdateCacher.getCachedValue(id);
                    const theMerge = lodashMerge(data, cachedUpdatedValue)
                    return theMerge;
                }
                ),
            );
        }
    }

    /**
     * This method uses the searchAll method with an different endpoint to use the old search way, useful to
     * retrieve paged data to keep in offline cache
     * @param queryText 
     * @param sortObject 
     * @param pageSize 
     * @param types 
     * @param page 
     * @param advancedSearches 
     * @param filteredIds 
     * @param pathAndValueToCheck 
     * @param options 
     */
    public synchronizeAll(queryText: string | null, sortObject: any, pageSize: number = 15, types?: string[], page: number = 0, advancedSearches: any = null, filteredIds?: string[], pathAndValueToCheck?: any, options: DataStoreOptions = {}): Observable<SearchResponse<S>> {
        return this.searchAll(queryText, sortObject, pageSize, types, page, advancedSearches, filteredIds, pathAndValueToCheck, "/indexSearch/dbSearch", options);
    }

    public searchAll(queryText: string | null, sortObject: any, pageSize: number = 15, types?: string[], page: number = 0, advancedSearches: any = null, filteredIds?: string[], pathAndValueToCheck?: any, searchEndpointOverride: string | null = null, options: DataStoreOptions = {})
        : Observable<SearchResponse<S>> {
        const endpoint = searchEndpointOverride || "/indexSearch/search";
        const searchTypes = types || this.typesForSearch // trying to get type name from endpoint (as they are suposed to be similar)
        const query = {
            queryText,
            sortObject,
            types: searchTypes,

            advancedSearches,
            excludedIds: null,
            filteredIds,
            group: this.userGroupId,
            language: FALLBACK_LOCALE,
            page,
            pageSize,
            pathAndValueToCheck,
            searchOnArchived: false,
            searchOnVersioned: false
        }
        const cacheKey = `${endpoint}?${searchTypes.toString()}?${query.toString()}`;
        // if online
        if (this.offlineDataFetcher && window.navigator.onLine) {
            return this.proxy.fetch(endpoint, undefined, 'POST', query).pipe(
                // tap(data => this.cache.write(cacheKey, data)),
                catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
                this.mergeUpdateCacheDataToSearchResults.bind(this),
                // Adding locally created entities to results
                switchMap( // TODO can be improved with sorting cached values and online values together
                    async data => {
                        let localEntities = await this.getLocallyCreatedCachedObjectsAsS();
                        if (localEntities.length > 0) {
                            if (!data.allData) {
                                data.allData = []
                            }

                            // Restraining to filtered ids if there are some
                            if (filteredIds) {
                                const matchingLocalEntities = await this.offlineDataFetcher.searchAll(query, localEntities, await this.getLocallyUpdatedCachedObjectsAsS(), true).pipe(first()).toPromise();
                                localEntities = matchingLocalEntities.allData;
                            }

                            data.allData.push(...localEntities);
                            data.totalSize += localEntities.length;
                        }
                        return data
                    }
                )
            )
        } else {
            return this.locallyCreatedEntitiesToData$()
                .pipe(
                    switchMap(
                        async data => {
                            const locallyUpdatedData = await this.getLocallyUpdatedCachedObjectsAsS();
                            return this.offlineDataFetcher.searchAll(query, data, locallyUpdatedData, undefined)
                        }
                    ),
                    switchMap(
                        data => from(data)
                    ),
                    this.mergeUpdateCacheDataToSearchResults.bind(this)
                )
        }
    }

    /**
     * Performs a simple text search  and returns the first result
     * Useful to search on exact item identifier for example
     * @param queryText 
     */
    public searchOneWithText(queryText: string, types: string[]): Promise<S> {
        return this.searchAll(queryText, null, undefined, types, 0, undefined, undefined, undefined, undefined, undefined).pipe(
            pluck('allData'),
            first(),
            map(
                searchResults => searchResults[0]
            )
        )
            .toPromise()
    }

    public searchTitles(types: string[], filterText: string, pageNumber: number = 0, defaultFilter: string = "", size: number = 10, filterIds?: string[], esPath?: any, language: string = FALLBACK_LOCALE) {
        if (window.navigator.onLine) {
            let searchOptions: any = {
                defaultFilter,
                esPath,
                filterText,
                language,
                pageNumber,
                size,
                types,
            }

            if (filterIds) {
                searchOptions = { ...searchOptions, filterIds }
            }
            return this.proxy.fetch("/indexSearch/title", searchOptions, "POST")
        } else {
            return from(this.offlineDataFetcher.searchTitles(types, filterText, pageNumber, defaultFilter, size, filterIds, esPath, language))
        }
    }

    public searchTitle(types: string[], id: string) {
        return this.searchTitles(
            types,
            "",
            0,
            undefined,
            1,
            [id]
        )
    }

    public notifyOn(pattern: RegExp) {
        return this.cache.notifyOn(pattern);
    }

    public update(id: string, body: Partial<T>, endpointOverride?: string) {
        // If online, update directly in backend
        if (window.navigator.onLine && !OfflineDataFetcher.isTemporaryId(id)) { // differentiate locally created entities from other entities
            // Online update
            const endpoint = endpointOverride || this.endpoint;
            const uri = `${endpoint}/${id}`;
            return this.proxy
                .fetch(uri, undefined, 'PUT', body, true)
                .pipe(
                    switchMap(response => this.fetchOne(id)),
                    // Remove corresponding in updateCache (if exists) to avoid two versions of the entity
                    tap(async data => {
                        const cachedUpdatedValue = await this.offlineDataUpdateCacher.getCachedValue(id)
                        if (cachedUpdatedValue) {
                            this.offlineDataUpdateCacher.removeCachedValue(id)
                        }

                        // update cached data from fetch to avoid to versions of the entitiy
                        this.offlineDataFetcher.updateOne(id, data as T);
                    }),
                    switchMap(() => this.fetchOne(id, true)));
        }
        // If offline, store in cache and return cached data merged with updated data
        else {
            this.offlineDataUpdateCacher.storeBeforePushing(id, body);
            return this.fetchOne(id);
        }
    }

    public create(createBody?: Partial<T>, body?: Partial<T>): Observable<T> {
        const uri = `${this.endpoint}`;
        if (window.navigator.onLine || !body) {
            return this.proxy.fetch(uri, createBody, 'POST').pipe(
                concatMap(response => {
                    if (!body) {
                        return of(response);
                    }
                    // In case of a locally created entity, replacing temporary id by the one from db
                    const id: string = _.get(body, "id")
                    if (id && OfflineDataFetcher.isTemporaryId(id)) {
                        Object.defineProperty(body, "id", { value: response.id })
                    }
                    return this.update(response.id, lodashMerge(response, body));
                }),
                switchMap(({ id }) => this.fetchOne(id)),
            );
        } else { // Offline mode
            // the '_' prefix of the id identifies the id as temporary in the cache keys
            const tempId = '_' + Math.random().toString(36).substr(2, 9) + Math.random().toString(36).substr(2, 9);
            Object.defineProperty(body, "id", {
                value: tempId
            })

            this.offlineDataUpdateCacher.storeBeforePushing(tempId, body);
            return of(body as T);
        }
    }

    public duplicate(entityToDuplicateId: string, createBody?: Partial<T>, body?: Partial<T>) {

        const duplicateUri = `${this.endpoint}/duplicate/${entityToDuplicateId}`;
        const getUri = `${this.endpoint}/${entityToDuplicateId}`

        return this.proxy.fetch(duplicateUri, createBody, 'POST').pipe(
            concatMap(response => {
                if (!body) {
                    return of(response);
                }
                return this.update(response.id, lodashMerge(response, body));
            }),
            switchMap(({ id }) => this.fetchOne(id))
        );
    }

    /**
     * Returns an observable that emits a boolean value for 
     * each push of an entity in the cache (true for success, false for failure)
     */
    public async pushCachedValues(pushNewEntities: boolean = true): Promise<Observable<boolean>> {
        this.globalPushing = true;
        let successCounter = 0;
        // getting ids of entities in cache)
        let allIds: string[] = await this.offlineDataUpdateCacher.getCachedObjectsKeys();
        // Remove new entities if necessary
        if (pushNewEntities) {
            allIds = allIds.filter(id => !id.includes('_'))
        }
        const idsObs = of(allIds);
        return idsObs.pipe(
            // Retrieving data to update from cache
            switchMap(
                async ids => {
                    const dataToUpdate: Array<CachedEntityData<T>> = [];
                    for (const id of ids) {
                        // Check if pushing has been interrupted
                        if (this.globalPushing) {
                            const sanitizedId = this.offlineDataUpdateCacher.getSanitizedId(id);
                            const body = await this.offlineDataUpdateCacher.getCachedValue(sanitizedId);

                            // Cleaning unwanted undefined cached values
                            if (sanitizedId === "undefined") {
                                await this.offlineDataUpdateCacher.removeCachedValue(sanitizedId);
                            }

                            // Don't push directly locally created entities (id starting with '_')
                            if (!OfflineDataFetcher.isTemporaryId(sanitizedId)) {
                                dataToUpdate.push({ id: sanitizedId, body });
                            }
                        }
                    }
                    // When there are only locally created entities and 'pushNewEntities' is false, we have to return something
                    // to notify that the push is finished eventhough nothing has been pushed directly (ex : auditRecords)
                    if (!pushNewEntities && dataToUpdate.length === 0) {
                        return of({} as any);
                    }
                    return from(dataToUpdate); // quand [] = :'(
                }
            ),
            // this switchMap is required to cancel the async of the previous callback, which
            // generates a Observable instead of the wanted data directly
            switchMap((data) => {
                return data;
            }),
            // Performing updates
            mergeMap(
                dataToUpdate => {
                    if (dataToUpdate.id && dataToUpdate.body) {
                        return this.update(
                            dataToUpdate.id,
                            dataToUpdate.body
                        )
                            .pipe(
                                first(),
                                // Handle success or error if the update and emit boolean
                                catchError(e => of(Error('Error during update...'))),
                                switchMap(
                                    response => {
                                        if (!(response instanceof Error)) {
                                            this.offlineDataUpdateCacher.removeCachedValue(dataToUpdate.id);
                                            successCounter++;
                                            return of(true)
                                        }
                                        return of(false);
                                    }
                                )
                            )
                    }
                    else {
                        return of(true);
                    }
                }
            ),
        )
    }

    public async getNbCachedObjects(countLocallyCreated: boolean = true) {
        return await this.offlineDataUpdateCacher.getNbCachedObjects(countLocallyCreated);
    }

    public async getObjectInUpdateCache(id: string) {
        return this.offlineDataUpdateCacher.getCachedValue(id)
    }

    public async removeObjectFromUpdateCache(id: string) {
        return this.offlineDataUpdateCacher.removeCachedValue(id)
    }

    public delete(id: string) {
        const uri = `${this.endpoint}/${id}`;

        const network$ = this.proxy.fetch(uri, undefined, "DELETE", undefined, undefined).pipe(
            last(),
            catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
        );
        return network$.pipe(distinctUntilChanged(isEqual));
    }

    public async synchronize(pageIndex: number, clearBefore?: boolean, firstCall: boolean = true) {
        if (firstCall) {
            this.executionStartTime = moment.now();
            this.abortSync = false;
            this.currentNbSychronized = 0;
            this.syncing = true;
            this.saveStats(DataSynchronisationStatus.SUCCESS);
        }
        if (!this.abortSync) {
            // Freeing last subscription
            if (this.currentSynchroSubscription) {
                this.currentSynchroSubscription.unsubscribe()
            }

            if (clearBefore) {
                this.offlineDataFetcher.clear()
                this.currentNbSychronized = 0;
            }

            this.currentSynchroSubscription =
                iif(() => window.navigator.onLine,
                    this.fetchPage(pageIndex),
                    throwError("Synchronization cannot be performed in offline mode")
                )
                    .pipe(
                        first(),
                        catchError(
                            (error) => {
                                this.saveStats(DataSynchronisationStatus.ERROR);
                                this.syncing = false;
                                dataSynchroEventBus$.next({
                                    name: this.mainCacheKey,
                                    status: DataSynchronisationStatus.ERROR,
                                })
                                throw error;
                            }
                        )
                    )
                    .subscribe(
                        async searchResponse => {

                            // Retrieving total number of results for stats
                            if (this.maxNbToSynchronize === Infinity) {
                                this.maxNbToSynchronize = searchResponse.totalSize
                            }

                            // Appending results if there are ones, ending syncing otherwise
                            if (searchResponse.results.length > 0) {
                                await this.offlineDataFetcher.append(searchResponse.results)
                                if (this.emulateSearch) {
                                    this.currentNbSychronized += 1;
                                } else {
                                    this.currentNbSychronized += searchResponse.results.length;
                                }
                                this.saveStats(DataSynchronisationStatus.SUCCESS);
                                this.synchronize(pageIndex++, false, false)
                            } else {
                                dataSynchroEventBus$.next({
                                    name: this.mainCacheKey,
                                    status: DataSynchronisationStatus.SUCCESS,
                                })
                                this.saveStats(DataSynchronisationStatus.SUCCESS);
                                this.syncing = false;
                            }
                        }
                    )
            pageIndex++;
        }
        else {
            this.saveStats(DataSynchronisationStatus.ABORTED);

            this.abortSync = false;
            this.syncing = false;
            dataSynchroEventBus$.next({
                name: this.mainCacheKey,
                status: DataSynchronisationStatus.ABORTED,
            })
            this.saveStats(DataSynchronisationStatus.ABORTED)
        }
    }

    public abortPush() {
        this.globalPushing = false;
    }

    public abortSynchronisation() {
        this.abortSync = true;
    }

    public async fetchNbEntitiesInUpdateCache() {
        this.nbEntitiesInUpdateCache = await this.getNbCachedObjects(true)
        this.nbCreatedEntitiesInUpdateCache = this.nbEntitiesInUpdateCache - await this.getNbCachedObjects(false)
    }

    public fetchOneFromPredicate(predicate: (data: T) => boolean) {
        if (window.navigator.onLine) {
            throw new Error("This method is not meant to be called in an online context");
        } else {
            return this.offlineDataFetcher.fetchOneFromPredicate(predicate)
        }
    }

    // tslint:disable-next-line:member-ordering
    protected convertTtoS(toConvert: T): S {
        return toConvert as any as S;
    }

    // tslint:disable-next-line:member-ordering
    protected convertStoT(toConvert: S): T {
        return toConvert as any as T;
    }

    protected initializeDataFetcher() {
        this.offlineDataFetcher = new OfflineDataFetcher<T, S>(this.sanitizedIdentifer);
    }

    protected initializeDataPusher() {
        this.offlineDataUpdateCacher = new OfflineDataUpdateCacher<T>(this.sanitizedIdentifer);
    }

    /**
     * Returns an the locally created entities as an array
     */
    private async getLocallyCreatedCachedObjectsAsS(): Promise<S[]> {
        return (await this.offlineDataUpdateCacher.getLocallyCreatedCachedObjects()).map(this.convertTtoS)
    }

    private async getLocallyUpdatedCachedObjectsAsS(): Promise<S[]> {
        return (await this.offlineDataUpdateCacher.getLocallyUpdatedCachedObjects()).map(this.convertTtoS)
    }

    /**
     * Returns an observable emitting the locally created entities as an array
     */
    private locallyCreatedEntitiesToData$(): Observable<S[]> {
        return from(this.getLocallyCreatedCachedObjectsAsS())
    }

    private saveStats(status: DataSynchronisationStatus) {
        this.offlineDataFetcher.saveStats({
            currentNbSychronized: this.currentNbSychronized,
            lastSynchroDate: moment.now(),
            lastSynchroDuration: moment().diff(this.executionStartTime),
            lastSynchroStatus: status,
            maxNbToSynchronize: this.maxNbToSynchronize,
        });
    }

    /**
     * Pipeable operator thats merges data in the update cache that have not been pushed yet
     * into the search result (either from sync-cache or directly network result)
     * Only for results of a search request
     * @param source$ 
     */
    private mergeUpdateCacheDataToSearchResults(source$: Observable<SearchResponse<S>>) {
        return source$.pipe(switchMap(
            // retrieving cache data that has not been pushed to keep the vue up to date
            async (data: SearchResponse<S>) => {
                const results = data.allData;
                if (results) {
                    for (let result of results) {
                        const cachedUpdatedValue = await this.offlineDataUpdateCacher.getCachedValue(_.get(result, 'id'));
                        if (cachedUpdatedValue) {
                            result = this.mergeCacheToEntity(result, cachedUpdatedValue)
                        }
                    }
                }
                return data
            }
        ))
    }

    private mergeCacheToEntity(entity: any, cachedValue: any) {
        const cachedCurrentState = _.get(cachedValue, 'workflowSchema.currentState')
        const mergedResult = lodashMerge(entity, cachedValue)

        if (cachedCurrentState) {
            _.set(mergedResult, 'workflowSchema.currentState', cachedCurrentState);
        }

        return mergedResult;
    }

    private mergeUpdateCacheDataToOneResult(source$: Observable<T>) {
        return source$.pipe(switchMap(
            // retrieving cache data that has not been pushed to keep the vue up to date
            async (data: any) => {
                const cachedUpdatedValue = await this.offlineDataUpdateCacher.getCachedValue(data.id);
                if (cachedUpdatedValue) {
                    data = lodashMerge(data, cachedUpdatedValue)
                }
                return data
            }
        ))
    }

    private cache$(key: string): Observable<T[]> {
        return this.cache.read(key).pipe(filter(data => data !== undefined)) as Observable<T[]>;
    }

    private fetchPage(pageIndex: number): Observable<SearchResponse<S>> {
        return this.synchronizeAll(
            "",
            null,
            SEARCH_PAGE_SIZE,
            this.typesForSearch,
            pageIndex,
            undefined,
            undefined,
            undefined,
            { emulateSearch: this.emulateSearch }
        )
    }
}
